"use client";

import { Plus } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useTranslations } from "next-intl";
import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useRef,
} from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import type { SkillDomain } from "@/generated/prisma/client";
import { cn } from "@/lib/utils";

import { useReveal } from "../../_lib/use-reveal";
import type { BentoCell } from "../_utils/bento";

// =============================================================================

const SPAN_CLASSES: Record<BentoCell["span"], string> = {
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  6: "lg:col-span-6",
  12: "lg:col-span-12",
};

const detailsVariants: Variants = {
  hidden: {
    transition: { staggerChildren: 0.02, staggerDirection: -1 },
  },
  visible: {
    // Once the logo has glided to the corner
    transition: { staggerChildren: 0.055, delayChildren: 0.2 },
  },
};

const detailVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
    filter: "blur(4px)",
    transition: { duration: 0.2 },
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: EASE_OUT_EXPO },
  },
};

// =============================================================================

export type BentoSkill = {
  id: string;
  slug: string;
  name: string;
  creator: string | null;
  releaseYear: number | null;
  domains: SkillDomain[];
  isLearning: boolean;
  description: string;
  /** Server-rendered logo (simple-icons or upload) */
  icon: ReactNode;
};

type SkillCardProps = {
  skill: BentoSkill;
  cell: BentoCell;
  /** Spans the two columns of medium screens (odd last card) */
  isWideOnTablet: boolean;
  isActive: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onToggle: () => void;
};

/**
 * Technology card. At rest, its logo alone. Active (hover, keyboard focus or
 * tap): the logo glides to the top-left corner and shrinks, then the name,
 * the creator, the release year, the description and the fields appear.
 */
export function SkillCard({
  skill,
  cell,
  isWideOnTablet,
  isActive,
  onActivate,
  onDeactivate,
  onToggle,
}: SkillCardProps) {
  const t = useTranslations("Skills");
  const { ref: cardRef, isRevealed } = useReveal<HTMLLIElement>();
  const lastPointer = useRef<string>("mouse");
  const isNarrow = cell.span <= 4;

  // Spotlight under the pointer, without re-rendering
  const onPointerMove = (event: PointerEvent<HTMLLIElement>) => {
    const card = cardRef.current;
    if (!card || event.pointerType !== "mouse") return;
    const rect = card.getBoundingClientRect();
    card.style.setProperty("--spot-x", `${event.clientX - rect.left}px`);
    card.style.setProperty("--spot-y", `${event.clientY - rect.top}px`);
  };

  const onPointerDown = (event: PointerEvent) => {
    lastPointer.current = event.pointerType;
  };

  const onPointerEnter = (event: PointerEvent) => {
    if (event.pointerType === "mouse") onActivate();
  };

  const onPointerLeave = (event: PointerEvent) => {
    if (event.pointerType === "mouse") onDeactivate();
  };

  // Touch and pen: a tap toggles the card (mouse users simply hover)
  const onClick = () => {
    if (lastPointer.current !== "mouse") onToggle();
  };

  const onFocus = (event: FocusEvent<HTMLLIElement>) => {
    if (event.currentTarget.matches(":focus-visible")) onActivate();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <motion.li
      ref={cardRef}
      id={skill.slug}
      tabIndex={0}
      aria-labelledby={`skill-${skill.slug}`}
      initial={{ opacity: 0, y: 48 }}
      animate={isRevealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 48 }}
      transition={{
        duration: 1,
        delay: cell.column * 0.08,
        ease: EASE_OUT_EXPO,
      }}
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onDeactivate}
      onKeyDown={onKeyDown}
      className={cn(
        "group relative h-76 scroll-mt-28 overflow-hidden rounded-[0.625rem] border border-foreground/15 outline-offset-2 select-none sm:h-80 lg:h-[clamp(18rem,22.6vw,20.375rem)] dark:border-foreground/20",
        SPAN_CLASSES[cell.span],
        isWideOnTablet && "sm:col-span-2"
      )}
    >
      {/* Lighter card while active */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-0 bg-linear-to-br from-surface-1 to-surface-1 opacity-0 shadow-[0_1.5rem_3rem_-1.5rem_rgb(0_0_0/0.25)] transition-opacity duration-500 ease-out-quart dark:from-surface-3 dark:to-surface-2",
          isActive && "opacity-100"
        )}
      />
      <span
        aria-hidden
        className={cn(
          "absolute inset-0 rounded-[inherit] border border-foreground/40 opacity-0 transition-opacity duration-500",
          isActive && "opacity-100"
        )}
      />
      {/* Spotlight following the pointer */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(24rem circle at var(--spot-x, 50%) var(--spot-y, 50%), color-mix(in srgb, var(--foreground) 7%, transparent), transparent 65%)",
        }}
      />

      {/* Logo: centered at rest, top-left corner when active */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute flex",
          isActive
            ? "inset-x-[clamp(1.25rem,1.7vw,1.5rem)] top-[clamp(1.25rem,1.7vw,1.5rem)] items-start justify-start"
            : "inset-0 items-center justify-center"
        )}
      >
        <motion.div
          layout
          transition={{
            layout: {
              duration: 0.7,
              ease: EASE_OUT_EXPO,
              delay: isActive ? 0 : 0.1,
            },
          }}
          className={cn(
            isActive ? "size-14" : "size-[clamp(4.5rem,7vw,6.25rem)]"
          )}
        >
          <motion.div
            className="size-full"
            initial={{ scale: 0.5, rotate: -14, opacity: 0 }}
            animate={
              isRevealed
                ? { scale: 1, rotate: 0, opacity: 1 }
                : { scale: 0.5, rotate: -14, opacity: 0 }
            }
            transition={{
              type: "spring",
              stiffness: 160,
              damping: 16,
              delay: 0.25 + cell.column * 0.08,
            }}
          >
            {skill.icon}
          </motion.div>
        </motion.div>
      </div>

      {/* Details */}
      <motion.div
        initial={false}
        animate={isActive ? "visible" : "hidden"}
        variants={detailsVariants}
        className="relative flex h-full flex-col p-[clamp(1.25rem,1.7vw,1.5rem)]"
      >
        <div className="flex min-h-14 items-start justify-between gap-3 pl-18">
          <motion.div variants={detailVariants} className="min-w-0 pt-1">
            <h2
              id={`skill-${skill.slug}`}
              className={cn(
                "line-clamp-2 leading-tight font-semibold wrap-break-word",
                isNarrow
                  ? "text-[1.2rem] lg:text-[1.125rem]"
                  : "text-[1.2rem] min-[22rem]:text-[1.375rem]"
              )}
            >
              {skill.name}
            </h2>
            {skill.creator && (
              <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-muted-foreground">
                {t("by", { creator: skill.creator })}
              </p>
            )}
          </motion.div>
          {skill.releaseYear && (
            <motion.p
              variants={detailVariants}
              className="shrink-0 pt-1 text-[0.95rem] text-muted-foreground tabular-nums"
            >
              <span className="sr-only">
                {t("releasedIn", { year: skill.releaseYear })}
              </span>
              <span aria-hidden>{skill.releaseYear}</span>
            </motion.p>
          )}
        </div>

        <motion.p
          variants={detailVariants}
          className={cn(
            "mt-5 leading-snug text-foreground/90",
            isNarrow
              ? "line-clamp-8 text-[0.9rem] lg:line-clamp-7"
              : "line-clamp-8 text-[0.95rem] sm:text-base"
          )}
        >
          {skill.description}
        </motion.p>

        {skill.domains.length > 0 && (
          <motion.p
            variants={detailVariants}
            className="mt-auto self-end pt-4 text-sm text-muted-foreground"
          >
            <span className="sr-only">{t("domainsLabel")}: </span>
            {skill.domains.map((domain) => t(`domains.${domain}`)).join(" | ")}
          </motion.p>
        )}
      </motion.div>

      {/* Rest state extras */}
      {skill.isLearning && (
        <span
          className={cn(
            "absolute top-4 right-4 flex items-center gap-2 rounded-full border border-line bg-background/60 px-2.5 py-1 text-xs backdrop-blur transition-opacity duration-300",
            isActive && "opacity-0"
          )}
        >
          <span className="size-1.5 animate-pulse-soft rounded-full bg-brand" />
          {t("learning")}
        </span>
      )}
      <span
        aria-hidden
        className={cn(
          "absolute right-4 bottom-4 hidden size-8 place-items-center rounded-full border border-line text-muted-foreground transition-[opacity,transform] duration-500 ease-out-expo pointer-coarse:grid",
          isActive && "rotate-45 opacity-0"
        )}
      >
        <Plus className="size-3.5" />
      </span>
    </motion.li>
  );
}

// =============================================================================
